const DISPLAY_CURRENCY_KEY = 'maabar_preferred_display_currency';
// v3 (2026-10-04): v2 cached the market feed's CNY; CNY is now the admin rate.
const DISPLAY_RATE_CACHE_KEY = 'maabar_display_currency_rates_v3';
// Both rates are ADMIN rates — one figure for the whole product (owner,
// 2026-10-04), traders and suppliers alike:
//   SAR per USD: admin_settings.usd_sar_rate via current_sar_rate('USD') —
//     the same rate an offer locks when it is accepted, so a browsing price
//     and a payable one agree. 3.79 is only the fallback before it loads.
//   CNY per USD: admin_settings.cny_usd_rate via current_cny_per_usd(). It
//     replaced a live market feed. 7.10 is only the fallback before it loads.
// A CNY amount shows in SAR as CNY ÷ CNY-per-USD × SAR-per-USD (CNY → USD →
// SAR) — convertCurrencyAmount and cnyToSar below are that one calculation.
// DISPLAY ONLY: a CNY offer is still not payable online (current_sar_rate
// returns NULL for CNY, so nothing CNY is ever locked or charged).
const DEFAULT_RATES = { USD: 1, SAR: 3.79, CNY: 7.1 };

export const DISPLAY_CURRENCIES = ['USD', 'SAR', 'CNY'];
export const DEFAULT_DISPLAY_CURRENCY = 'USD';

export function normalizeDisplayCurrency(value) {
  return DISPLAY_CURRENCIES.includes(value) ? value : DEFAULT_DISPLAY_CURRENCY;
}

export function getStoredDisplayCurrency() {
  try {
    return normalizeDisplayCurrency(localStorage.getItem(DISPLAY_CURRENCY_KEY));
  } catch {
    return DEFAULT_DISPLAY_CURRENCY;
  }
}

export function storeDisplayCurrency(value) {
  try {
    localStorage.setItem(DISPLAY_CURRENCY_KEY, normalizeDisplayCurrency(value));
  } catch {
    // no-op
  }
}

export function getCachedDisplayRates() {
  try {
    const raw = localStorage.getItem(DISPLAY_RATE_CACHE_KEY);
    if (!raw) return DEFAULT_RATES;
    const parsed = JSON.parse(raw);
    return {
      ...DEFAULT_RATES,
      ...parsed,
    };
  } catch {
    return DEFAULT_RATES;
  }
}

export function storeDisplayRates(rates) {
  try {
    localStorage.setItem(DISPLAY_RATE_CACHE_KEY, JSON.stringify({
      ...DEFAULT_RATES,
      ...rates,
    }));
  } catch {
    // no-op
  }
}

// Both admin rates from the database. Each falls back on its own (to the
// cached value), and a failure is logged (security rule 4) rather than
// swallowed.
export async function fetchDisplayRates(sb) {
  const cached = getCachedDisplayRates();
  let sar = cached.SAR;
  let cny = cached.CNY;
  if (sb) {
    const [sarRes, cnyRes] = await Promise.all([
      sb.rpc('current_sar_rate', { p_currency: 'USD' }),
      sb.rpc('current_cny_per_usd'),
    ]);
    if (sarRes.error) console.error('[displayCurrency] current_sar_rate failed:', sarRes.error.code || '', sarRes.error.message);
    else if (Number(sarRes.data) > 0) sar = Number(sarRes.data);
    if (cnyRes.error) console.error('[displayCurrency] current_cny_per_usd failed:', cnyRes.error.code || '', cnyRes.error.message);
    else if (Number(cnyRes.data) > 0) cny = Number(cnyRes.data);
  }
  const rates = { USD: 1, SAR: sar, CNY: cny };
  storeDisplayRates(rates);
  return rates;
}

// CNY → SAR through USD: CNY ÷ (CNY per USD) × (SAR per USD), both admin
// rates. The catalogue helpers use this; convertCurrencyAmount does the same
// arithmetic (value / rates.CNY * rates.SAR) for every other price.
export function cnyToSar(amount, rates = DEFAULT_RATES) {
  const cnyPerUsd = (rates && rates.CNY) || DEFAULT_RATES.CNY;
  const sarPerUsd = (rates && rates.SAR) || DEFAULT_RATES.SAR;
  return (Number(amount) / cnyPerUsd) * sarPerUsd;
}

export function convertCurrencyAmount(amount, sourceCurrency, targetCurrency, rates = DEFAULT_RATES) {
  const value = Number(amount || 0);
  const source = normalizeDisplayCurrency(sourceCurrency || 'USD');
  const target = normalizeDisplayCurrency(targetCurrency || source);
  const sourceRate = rates?.[source] || DEFAULT_RATES[source] || 1;
  const targetRate = rates?.[target] || DEFAULT_RATES[target] || 1;

  if (!Number.isFinite(value) || value <= 0) return 0;
  if (!sourceRate || !targetRate) return value;
  if (source === target) return value;

  const usdValue = value / sourceRate;
  return usdValue * targetRate;
}

// Convert a FREE-TEXT catalog price to SAR. These prices come straight from the
// Gemini catalog extraction in whatever currency the source listing printed —
// mostly USD ("$4.99", "US$0.60", ranges "$1.40-$1.81", tiered
// "$1.50 / $1.30 / $1.1", per-pack "16-Piece: $10.00"), some RMB ("¥390"), and
// a few with a mislabeled `currency` column. We pick the source currency from
// the text symbols first, then the stored `currency`, and convert every
// price-anchored amount to SAR at the ADMIN rate. Quantities inside tier labels
// ("(1-49 PCS)") are left intact. Western digits + "ر.س".
// SAR ONLY, in every language (owner, 2026-09-29: the catalogue is trader-
// and visitor-facing, and the rate is internal). A CNY price converts CNY →
// USD → SAR at the two admin rates (2026-10-04); before that it returned ''.
// A text that mixes a yuan mark with a dollar mark still returns '' — there
// is no telling which figure is which.
export function catalogPriceToSAR(text, currency, rates = DEFAULT_RATES) {
  const t = String(text || '').trim();
  if (!t || t === 'not_found') return '';

  const sarPerUsd = (rates && rates.SAR) || DEFAULT_RATES.SAR;

  const hasYuan = /[¥￥元]|RMB|CNY/i.test(t);
  const hasDollar = /\$|USD/i.test(t);
  let src;
  if (hasYuan && !hasDollar) src = 'CNY';
  else if (hasDollar) src = 'USD';
  else {
    const c = String(currency || '').toUpperCase();
    src = (c === 'RMB' || c === 'CNY') ? 'CNY' : (c === 'SAR') ? 'SAR' : 'USD';
  }
  if (hasYuan && hasDollar) return '';

  const toSar = (n) => {
    const v = parseFloat(String(n).replace(/,/g, ''));   // strip thousands separators
    if (!Number.isFinite(v)) return null;
    const sar = src === 'SAR' ? v : src === 'CNY' ? cnyToSar(v, rates) : v * sarPerUsd;
    return `${Math.round(sar).toLocaleString('en-US')} ر.س`;
  };

  // Symbol-anchored amounts convert; tier quantities (no symbol) survive.
  if (hasYuan) {
    // ¥390 / RMB¥390 / RMB 390 before the number, 390元 / 390 RMB after it.
    return t.replace(/(?:(?:RMB|CNY)\s?[¥￥]?|[¥￥])\s?([\d,]+(?:\.\d+)?)|([\d,]+(?:\.\d+)?)\s?(?:元|RMB|CNY)/gi,
      (m, a, b) => toSar(a || b) || m).replace(/\b(?:RMB|CNY)\b|元/gi, '').replace(/\s{2,}/g, ' ').trim();
  }
  // Only a "$" anchors amounts: "12 USD" has no symbol, so it takes the
  // bare-amount path below (it used to come back as an unconverted "12").
  if (/\$/.test(t)) {
    return t.replace(/(?:US)?\$\s?([\d,]+(?:\.\d+)?)/gi, (m, d) => toSar(d) || m).replace(/\bUSD\b/gi, '').trim();
  }
  // No currency symbol: drop stray currency words, then convert the bare amounts —
  // but leave numbers inside parentheses alone. Tiered catalog prices label each
  // amount with a size/qty in parens ("565 (30in), 879 (70in)"); those are
  // dimensions, not prices, and must not be converted to SAR.
  return t
    .replace(/\b(?:SAR|USD|RMB|CNY)\b|ر\.?\s?س/gi, '')
    .replace(/(\([^)]*\))|[\d,]+(?:\.\d+)?/g, (m, paren) => paren || (toSar(m) || m))
    .replace(/\s{2,}/g, ' ')
    .trim();
}

// Catalogue price for traders and visitors: SAR only, in every language, at
// the admin rate (the one an accepted offer locks). Uses the NORMALIZED
// numeric fields (price_amount / price_max / price_currency, parsed once at
// import) for a single clean price or range, and falls back to the free-text
// parser only for rows that predate normalization. Returns null when there is
// no price to show ("On request"). CNY converts via cnyToSar (see above).
// `_isAr` is kept for call-site compatibility; the answer no longer depends on it.
export function formatCatalogPrice(p, _isAr, rates = DEFAULT_RATES) {
  const amt = p && p.price_amount != null ? Number(p.price_amount) : NaN;
  if (!Number.isFinite(amt) || amt <= 0) {
    const v = String((p && p.price) || '').trim();
    if (!v || v === 'not_found') return null;
    return catalogPriceToSAR(v, p && p.currency, rates) || null;
  }
  const c = String(p.price_currency || p.currency || 'USD').toUpperCase();
  const src = (c === 'RMB' || c === 'CNY') ? 'CNY' : (c === 'SAR') ? 'SAR' : 'USD';
  const maxN = p.price_max != null ? Number(p.price_max) : NaN;
  const hasMax = Number.isFinite(maxN) && maxN > amt;
  const sarPerUsd = (rates && rates.SAR) || DEFAULT_RATES.SAR;
  const toSar = (n) => Math.round(src === 'SAR' ? n : src === 'CNY' ? cnyToSar(n, rates) : n * sarPerUsd);
  const lo = toSar(amt).toLocaleString('en-US');
  return hasMax ? `${lo} - ${toSar(maxN).toLocaleString('en-US')} ر.س` : `${lo} ر.س`;
}

// The catalogue price as the SOURCE listing gave it — for ADMIN screens only
// (admins see everything: the original amount and currency).
export function formatCatalogPriceSource(p) {
  const amt = p && p.price_amount != null ? Number(p.price_amount) : NaN;
  if (!Number.isFinite(amt) || amt <= 0) {
    const v = String((p && p.price) || '').trim();
    if (!v || v === 'not_found') return null;
    const cur = String((p && p.currency) || '').trim();
    return cur && !v.toLowerCase().includes(cur.toLowerCase()) ? `${v} ${cur}` : v;
  }
  const c = String(p.price_currency || p.currency || 'USD').toUpperCase();
  const src = (c === 'RMB' || c === 'CNY') ? 'CNY' : (c === 'SAR') ? 'SAR' : 'USD';
  const maxN = p.price_max != null ? Number(p.price_max) : NaN;
  const hasMax = Number.isFinite(maxN) && maxN > amt;
  const num = (n) => Number(n).toLocaleString('en-US', { maximumFractionDigits: 2 });
  const sym = src === 'CNY' ? '¥' : src === 'SAR' ? 'SAR ' : '$';
  return hasMax ? `${sym}${num(amt)} - ${sym}${num(maxN)}` : `${sym}${num(amt)}`;
}

export function formatCurrencyAmount(amount, currency, lang = 'en', options = {}) {
  const value = Number(amount || 0);
  if (!Number.isFinite(value)) return '—';

  // ar-SA-u-nu-latn: Arabic locale grouping/decimal punctuation, Latin digits.
  const locale = lang === 'ar' ? 'ar-SA-u-nu-latn' : lang === 'zh' ? 'zh-CN' : 'en-US';
  return `${value.toLocaleString(locale, {
    minimumFractionDigits: options.minimumFractionDigits ?? 0,
    maximumFractionDigits: options.maximumFractionDigits ?? 2,
  })} ${normalizeDisplayCurrency(currency)}`;
}

export function buildDisplayPrice({ amount, sourceCurrency, displayCurrency, rates, lang = 'en' }) {
  const source = normalizeDisplayCurrency(sourceCurrency || 'USD');
  const target = normalizeDisplayCurrency(displayCurrency || source);
  const displayAmount = convertCurrencyAmount(amount, source, target, rates);

  return {
    sourceCurrency: source,
    displayCurrency: target,
    sourceAmount: Number(amount || 0),
    displayAmount,
    formattedDisplay: formatCurrencyAmount(displayAmount, target, lang),
    formattedSource: formatCurrencyAmount(amount, source, lang),
    isConverted: source !== target,
  };
}

// Renders an amount in its source currency, optionally followed by an
// "≈ converted" tail when the viewer's display currency differs.
// Single row when source === display, both rows otherwise.
export function formatPriceWithConversion({ amount, sourceCurrency, displayCurrency, rates, lang = 'en', separator = ' ≈ ', options }) {
  const built = buildDisplayPrice({ amount, sourceCurrency, displayCurrency, rates, lang });
  const primary = formatCurrencyAmount(built.sourceAmount, built.sourceCurrency, lang, options);
  if (!built.isConverted) return primary;
  const secondary = formatCurrencyAmount(built.displayAmount, built.displayCurrency, lang, options);
  return `${primary}${separator}${secondary}`;
}

export async function persistDisplayCurrencyPreference({ sb, userId, currency, setProfile }) {
  const normalized = normalizeDisplayCurrency(currency);
  storeDisplayCurrency(normalized);

  if (!sb || !userId) {
    return { persistedRemotely: false, currency: normalized };
  }

  const { error } = await sb
    .from('profiles')
    .update({ preferred_display_currency: normalized })
    .eq('id', userId);

  if (!error) {
    setProfile?.(prev => prev ? { ...prev, preferred_display_currency: normalized } : prev);
    return { persistedRemotely: true, currency: normalized };
  }

  const missingColumn = /column profiles\.(preferred_display_currency) does not exist/i.test(error.message || '');
  return {
    persistedRemotely: false,
    currency: normalized,
    error,
    needsMigration: missingColumn,
  };
}
